import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import PageHeader from "../../../components/layout/PageHeader";
import { Button } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { AuthUtils, logout, useAdmin } from "../../auth";

/** One fact about the account, as a labelled row of the card. */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-3 border-b border-line last:border-0">
    <span className="w-full sm:w-[180px] flex-shrink-0 text-[13px] text-ink-muted">
      {label}
    </span>
    <span className="text-[14px] text-ink break-words">{value}</span>
  </div>
);

/**
 * The account, and the way out of it.
 *
 * Signing out lives here rather than in the rail, so the one thing that ends a
 * session sits with the rest of what can be done to an account instead of a
 * click away from every screen. It asks first — a mis-click on a rail item
 * should not cost somebody their session.
 */
const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin, email } = useAdmin();
  const [confirming, setConfirming] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    const refreshToken = AuthUtils.getRefreshToken();
    try {
      if (refreshToken) await logout(refreshToken);
    } catch {
      // Ending the session locally regardless — a dead network is not a
      // reason to trap someone in a session they clicked to leave.
    }
    // Which takes the name with it: the session is where it lives, so there is
    // no cached profile left behind for the next admin to sign in under.
    AuthUtils.clearSession();
    navigate(ROUTES.login, { replace: true });
  };

  return (
    <div className="min-h-full flex flex-col px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Settings" />

      <section className="mt-5 rounded-md border border-line bg-white p-4 sm:p-6">
        <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
          Your account
        </h2>

        {/* Name, address and role — the whole of what the panel is told about
            whoever is signed in. There was an "Admin since" row here too; it
            was read from a profile endpoint that does not exist, so it printed
            an em dash on every account forever. It comes back the day the API
            has a date to put in it. */}
        <div className="mt-4">
          <DetailRow label="Name" value={admin?.name || "—"} />
          <DetailRow label="Email" value={admin?.email || email || "—"} />
          {/* A label, not a lookup — see the rail's foot. */}
          <DetailRow label="Role" value="Admin" />
        </div>
      </section>

      <section className="mt-5 rounded-md border border-line bg-white p-4 sm:p-6">
        <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
          Sign out
        </h2>
        <p className="mt-1 text-[14px] text-ink-muted leading-[1.6]">
          Ends this session on this browser. You will need your password and a
          fresh verification code to sign back in.
        </p>

        {confirming ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 animate-confirm-in">
            <span className="text-[14px] text-ink">Sign out of the panel?</span>
            <Button
              size="sm"
              variant="danger"
              loading={signingOut}
              onClick={handleSignOut}
            >
              Yes, sign out
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={signingOut}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="dangerQuiet"
            className="mt-4"
            startIcon={<LogOut className="w-4 h-4" />}
            onClick={() => setConfirming(true)}
          >
            Sign out
          </Button>
        )}
      </section>
    </div>
  );
};

export default SettingsPage;
